import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { 
  Send, CheckCircle2, Clock, Eye, FileSignature, 
  AlertCircle, Loader2, RefreshCw, ExternalLink, 
  FileText, User, Mail, Users, ArrowRight, Shield,
  Plus, File, X
} from "lucide-react";
import DocumentPreviewModal from "./DocumentPreviewModal";

// Status configuration
const ENVELOPE_STATUSES = {
  not_sent: { label: "Not Sent", color: "bg-slate-100 text-slate-700", icon: Clock },
  sent: { label: "Sent", color: "bg-blue-100 text-blue-700", icon: Send },
  delivered: { label: "Delivered", color: "bg-cyan-100 text-cyan-700", icon: CheckCircle2 },
  viewed: { label: "Viewed", color: "bg-amber-100 text-amber-700", icon: Eye },
  completed: { label: "Completed", color: "bg-green-100 text-green-700", icon: FileSignature },
  declined: { label: "Declined", color: "bg-red-100 text-red-700", icon: AlertCircle },
  voided: { label: "Voided", color: "bg-gray-100 text-gray-700", icon: AlertCircle },
};

const DocuSignTracker = ({ proposalId, proposal, token, onUpdate }) => {
  const [envelopeStatus, setEnvelopeStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [agreementDetails, setAgreementDetails] = useState(null);
  const [allDocuments, setAllDocuments] = useState([]);
  const [selectedSupportingDocs, setSelectedSupportingDocs] = useState([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [showDocPicker, setShowDocPicker] = useState(false);
  const [showDocumentPreview, setShowDocumentPreview] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };
  const docusignConfig = proposal?.docusign_config || {};
  const hasAgreement = !!docusignConfig.agreement_doc_id;
  const autoSendEnabled = docusignConfig.auto_send_on_accept;
  const signingMode = docusignConfig.signing_mode || "in_order"; // "in_order" or "all_at_once"

  // Fetch envelope status
  const fetchStatus = async (showRefreshState = false) => {
    if (showRefreshState) setRefreshing(true);
    try {
      const response = await axios.get(
        `${API}/docusign/proposals/${proposalId}/envelope-status`,
        { headers, withCredentials: true }
      );
      setEnvelopeStatus(response.data);
    } catch (error) {
      console.error("Failed to fetch envelope status:", error);
      setEnvelopeStatus({ status: "error", error: error.response?.data?.detail || "Failed to load" });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (proposalId) {
      fetchStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposalId]);

  // Fetch agreement details and all documents for preview
  const fetchAgreementDetails = async () => {
    if (!docusignConfig.agreement_doc_id) return;
    
    setLoadingDetails(true);
    try {
      // Fetch the main agreement
      const agreementRes = await axios.get(
        `${API}/docusign/documents/${docusignConfig.agreement_doc_id}`,
        { headers, withCredentials: true }
      );
      setAgreementDetails(agreementRes.data);
      
      // Fetch all documents for the picker
      const allDocsRes = await axios.get(
        `${API}/docusign/documents`,
        { headers, withCredentials: true }
      );
      setAllDocuments(allDocsRes.data);
      
      // Initialize selected supporting docs from config
      setSelectedSupportingDocs(docusignConfig.supporting_doc_ids || []);
    } catch (error) {
      console.error("Failed to fetch agreement details:", error);
      toast.error("Failed to load agreement details");
    } finally {
      setLoadingDetails(false);
    }
  };

  // Open preview modal
  const openPreviewModal = async () => {
    setShowPreviewModal(true);
    if (!agreementDetails) {
      await fetchAgreementDetails();
    }
  };

  // Preview document in new tab
  const previewDocument = (docId) => {
    window.open(`${API}/docusign/documents/${docId}/preview`, '_blank');
  };

  // Toggle supporting document selection
  const toggleSupportingDoc = (docId) => {
    setSelectedSupportingDocs(prev => 
      prev.includes(docId) 
        ? prev.filter(id => id !== docId)
        : [...prev, docId]
    );
  };

  // Build signer list from docusign_config
  const getSignersList = () => {
    const signers = [];
    
    // Client signers
    const clientSigners = docusignConfig.client_signers || [];
    clientSigners.forEach((cs, idx) => {
      if (cs.email && (cs.first_name || cs.last_name)) {
        signers.push({
          type: "Client",
          name: `${cs.first_name || ''} ${cs.last_name || ''}`.trim(),
          email: cs.email,
          order: cs.order || idx + 1
        });
      }
    });

    // Additional signers (compliance, etc.)
    const additionalSigners = docusignConfig.additional_signers || [];
    additionalSigners.forEach((as) => {
      if (as.email && (as.first_name || as.last_name)) {
        signers.push({
          type: as.role_title || "Additional Signer",
          name: `${as.first_name || ''} ${as.last_name || ''}`.trim(),
          email: as.email,
          order: as.order || 50
        });
      }
    });

    // Advisor signer
    const advisorSigner = docusignConfig.advisor_signer;
    if (advisorSigner && advisorSigner.email) {
      signers.push({
        type: "Advisor",
        name: `${advisorSigner.first_name || ''} ${advisorSigner.last_name || ''}`.trim(),
        email: advisorSigner.email,
        order: 100 // Always last
      });
    }

    // Sort by order only if signing is sequential
    if (signingMode === "in_order") {
      return signers.sort((a, b) => a.order - b.order);
    }
    return signers;
  };

  // Send envelope after confirmation
  const confirmAndSend = async () => {
    setSending(true);
    try {
      const response = await axios.post(
        `${API}/docusign/proposals/${proposalId}/send-envelope`,
        { supporting_doc_ids: selectedSupportingDocs },
        { headers, withCredentials: true }
      );
      
      toast.success("DocuSign envelope sent successfully!");
      setEnvelopeStatus({
        status: "sent",
        envelope_id: response.data.envelope_id,
        sent_at: new Date().toISOString()
      });
      
      setShowPreviewModal(false);
      if (onUpdate) onUpdate();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to send envelope";
      toast.error(errorMsg);
    } finally {
      setSending(false);
    }
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleString();
  };

  // Get supporting documents that are available (not the main agreement)
  const getAvailableSupportingDocs = () => {
    return allDocuments.filter(doc => 
      doc.doc_id !== docusignConfig.agreement_doc_id
    );
  };

  if (loading) {
    return (
      <Card className="border-stone">
        <CardContent className="py-8">
          <div className="flex items-center justify-center gap-2 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>Loading DocuSign status...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  const status = envelopeStatus?.status || "not_sent";
  const statusConfig = ENVELOPE_STATUSES[status] || ENVELOPE_STATUSES.not_sent;
  const StatusIcon = statusConfig.icon;
  const envelopeSent = status !== "not_sent" && status !== "error";
  const signersList = getSignersList();
  const availableSupportingDocs = getAvailableSupportingDocs();

  return (
    <>
      <Card className="border-stone" data-testid="docusign-tracker">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="font-display text-xl flex items-center gap-2">
              <FileSignature className="w-5 h-5 text-forest" />
              DocuSign
            </CardTitle>
            {envelopeSent && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => fetchStatus(true)}
                disabled={refreshing}
                data-testid="refresh-status-btn"
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              </Button>
            )}
          </div>
          {!hasAgreement && (
            <CardDescription className="text-amber-600">
              No agreement was configured for this proposal
            </CardDescription>
          )}
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Status Badge */}
          <div className="flex items-center gap-3">
            <Badge className={`${statusConfig.color} flex items-center gap-1.5 px-3 py-1`}>
              <StatusIcon className="w-4 h-4" />
              {statusConfig.label}
            </Badge>
            {envelopeStatus?.sent_at && (
              <span className="text-sm text-muted-foreground">
                Sent: {formatDate(envelopeStatus.sent_at)}
              </span>
            )}
          </div>

          {/* Not Sent State - Show Preview & Send Button */}
          {status === "not_sent" && hasAgreement && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                {autoSendEnabled 
                  ? "Auto-send was enabled but the envelope wasn't sent. Review and send manually."
                  : "Auto-send was not enabled. Review the details before sending."
                }
              </p>
              
              {/* Quick summary of signers */}
              {signersList.length > 0 && (
                <div className="bg-sand/50 rounded-lg p-3">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-2">
                    {signingMode === "all_at_once" ? (
                      <>
                        <Users className="w-3 h-3" />
                        {signersList.length} Signers (All at once)
                      </>
                    ) : (
                      <>
                        <ArrowRight className="w-3 h-3" />
                        {signersList.length} Signers (In order)
                      </>
                    )}
                  </p>
                  <div className="space-y-1">
                    {signersList.slice(0, 2).map((signer, idx) => (
                      <div key={idx} className="text-sm flex items-center gap-2">
                        <User className="w-3 h-3 text-muted-foreground" />
                        <span>{signer.name}</span>
                        <span className="text-muted-foreground">({signer.type})</span>
                      </div>
                    ))}
                    {signersList.length > 2 && (
                      <p className="text-xs text-muted-foreground">
                        + {signersList.length - 2} more...
                      </p>
                    )}
                  </div>
                </div>
              )}

              <Button
                className="w-full bg-forest hover:bg-forest/90"
                onClick={openPreviewModal}
                data-testid="review-send-docusign-btn"
              >
                <Eye className="w-4 h-4 mr-2" />
                Review & Send
              </Button>
            </div>
          )}

          {/* No Agreement Configured */}
          {status === "not_sent" && !hasAgreement && (
            <p className="text-sm text-muted-foreground">
              This proposal was accepted without a DocuSign agreement configured. 
              You'll need to handle the signing process manually.
            </p>
          )}

          {/* Envelope Sent - Show Tracking Info */}
          {envelopeSent && (
            <div className="space-y-4">
              {/* Progress Tracker */}
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <div className={`flex flex-col items-center ${['sent', 'delivered', 'viewed', 'completed'].includes(status) ? 'text-forest' : ''}`}>
                  <div className={`w-3 h-3 rounded-full ${['sent', 'delivered', 'viewed', 'completed'].includes(status) ? 'bg-forest' : 'bg-slate-200'}`} />
                  <span className="mt-1">Sent</span>
                </div>
                <div className={`flex-1 h-0.5 mx-2 ${['delivered', 'viewed', 'completed'].includes(status) ? 'bg-forest' : 'bg-slate-200'}`} />
                <div className={`flex flex-col items-center ${['delivered', 'viewed', 'completed'].includes(status) ? 'text-forest' : ''}`}>
                  <div className={`w-3 h-3 rounded-full ${['delivered', 'viewed', 'completed'].includes(status) ? 'bg-forest' : 'bg-slate-200'}`} />
                  <span className="mt-1">Delivered</span>
                </div>
                <div className={`flex-1 h-0.5 mx-2 ${['viewed', 'completed'].includes(status) ? 'bg-forest' : 'bg-slate-200'}`} />
                <div className={`flex flex-col items-center ${['viewed', 'completed'].includes(status) ? 'text-forest' : ''}`}>
                  <div className={`w-3 h-3 rounded-full ${['viewed', 'completed'].includes(status) ? 'bg-forest' : 'bg-slate-200'}`} />
                  <span className="mt-1">Viewed</span>
                </div>
                <div className={`flex-1 h-0.5 mx-2 ${status === 'completed' ? 'bg-forest' : 'bg-slate-200'}`} />
                <div className={`flex flex-col items-center ${status === 'completed' ? 'text-forest' : ''}`}>
                  <div className={`w-3 h-3 rounded-full ${status === 'completed' ? 'bg-forest' : 'bg-slate-200'}`} />
                  <span className="mt-1">Signed</span>
                </div>
              </div>

              {/* Recipients Status */}
              {envelopeStatus?.recipients && envelopeStatus.recipients.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Recipients</h4>
                  <div className="space-y-1.5">
                    {envelopeStatus.recipients.map((recipient, idx) => (
                      <div key={idx} className="flex items-center justify-between text-sm bg-sand/30 rounded px-2 py-1.5">
                        <div className="flex items-center gap-2">
                          <span className="w-5 h-5 rounded-full bg-forest/10 text-forest text-xs flex items-center justify-center">
                            {recipient.routing_order || idx + 1}
                          </span>
                          <span className="font-medium truncate max-w-[120px]">{recipient.name}</span>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          recipient.status === 'completed' || recipient.status === 'signed' 
                            ? 'bg-green-100 text-green-700' 
                            : recipient.status === 'delivered' 
                            ? 'bg-blue-100 text-blue-700'
                            : recipient.status === 'sent'
                            ? 'bg-amber-100 text-amber-700'
                            : 'bg-slate-100 text-slate-600'
                        }`}>
                          {recipient.status === 'completed' || recipient.status === 'signed' ? 'Signed' : 
                           recipient.status === 'delivered' ? 'Delivered' : 
                           recipient.status === 'sent' ? 'Pending' : recipient.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Envelope Details */}
              <div className="bg-sand/50 rounded-lg p-3 space-y-2 text-sm">
                {envelopeStatus?.envelope_id && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Envelope ID</span>
                    <span className="font-mono text-xs">{envelopeStatus.envelope_id.slice(0, 8)}...</span>
                  </div>
                )}
                {envelopeStatus?.status_changed_at && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Last Update</span>
                    <span>{formatDate(envelopeStatus.status_changed_at)}</span>
                  </div>
                )}
                {envelopeStatus?.completed_at && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Completed</span>
                    <span className="text-green-600 font-medium">{formatDate(envelopeStatus.completed_at)}</span>
                  </div>
                )}
                {envelopeStatus?.voided_reason && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Voided Reason</span>
                    <span className="text-red-600">{envelopeStatus.voided_reason}</span>
                  </div>
                )}
              </div>

              {/* View in DocuSign Link */}
              {envelopeStatus?.envelope_id && (
                <Button
                  variant="outline"
                  className="w-full"
                  asChild
                >
                  <a 
                    href={`https://appdemo.docusign.com/documents/details/${envelopeStatus.envelope_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    View in DocuSign
                  </a>
                </Button>
              )}
            </div>
          )}

          {/* Error State */}
          {status === "error" && (
            <div className="text-sm text-red-600">
              <AlertCircle className="w-4 h-4 inline mr-2" />
              {envelopeStatus?.error || "Failed to load status"}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Preview & Confirmation Modal */}
      <Dialog open={showPreviewModal} onOpenChange={setShowPreviewModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Shield className="w-5 h-5 text-forest" />
              Review Before Sending
            </DialogTitle>
            <DialogDescription>
              Please review all details carefully. This will be sent for signature.
            </DialogDescription>
          </DialogHeader>

          {loadingDetails ? (
            <div className="py-8 flex items-center justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-forest" />
            </div>
          ) : (
            <div className="space-y-6 py-4">
              {/* Main Document */}
              <div className="space-y-2">
                <h3 className="font-semibold flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Document
                </h3>
                <div className="bg-sand/50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">
                        {agreementDetails?.display_name || agreementDetails?.filename || "Agreement Document"}
                      </p>
                      {agreementDetails?.uploaded_at && (
                        <p className="text-sm text-muted-foreground mt-1">
                          Uploaded: {new Date(agreementDetails.uploaded_at).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowDocumentPreview(true)}
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      Preview
                    </Button>
                  </div>
                </div>
              </div>

              {/* Supporting Documents */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold flex items-center gap-2">
                    <File className="w-4 h-4" />
                    Additional Documents ({selectedSupportingDocs.length})
                  </h3>
                  {availableSupportingDocs.length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowDocPicker(!showDocPicker)}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add
                    </Button>
                  )}
                </div>
                
                {/* Document Picker */}
                {showDocPicker && availableSupportingDocs.length > 0 && (
                  <div className="border border-stone rounded-lg p-3 space-y-2 bg-white">
                    <p className="text-xs text-muted-foreground mb-2">Select documents to include:</p>
                    {availableSupportingDocs.map(doc => (
                      <div key={doc.doc_id} className="flex items-center gap-3 p-2 hover:bg-sand/30 rounded">
                        <Checkbox
                          id={doc.doc_id}
                          checked={selectedSupportingDocs.includes(doc.doc_id)}
                          onCheckedChange={() => toggleSupportingDoc(doc.doc_id)}
                        />
                        <label htmlFor={doc.doc_id} className="flex-1 text-sm cursor-pointer">
                          {doc.display_name || doc.filename}
                        </label>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2"
                          onClick={() => previewDocument(doc.doc_id)}
                        >
                          <Eye className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Selected Documents List */}
                {selectedSupportingDocs.length > 0 ? (
                  <div className="space-y-2">
                    {selectedSupportingDocs.map(docId => {
                      const doc = allDocuments.find(d => d.doc_id === docId);
                      if (!doc) return null;
                      return (
                        <div key={docId} className="bg-sand/50 rounded-lg p-3 flex items-center justify-between">
                          <span className="text-sm">{doc.display_name || doc.filename}</span>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 px-2"
                              onClick={() => previewDocument(docId)}
                            >
                              <Eye className="w-3 h-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 px-2 text-destructive"
                              onClick={() => toggleSupportingDoc(docId)}
                            >
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground bg-sand/30 rounded-lg p-3">
                    No additional documents selected. Click "Add" to include supporting documents.
                  </p>
                )}
              </div>

              {/* Proposal Info */}
              <div className="space-y-2">
                <h3 className="font-semibold">Proposal Details</h3>
                <div className="bg-sand/50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Title</span>
                    <span className="font-medium">{proposal?.title || "Untitled"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Prospect</span>
                    <span>{proposal?.prospect_name || "Unknown"}</span>
                  </div>
                  {proposal?.total_amount > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Value</span>
                      <span className="font-mono">${proposal.total_amount.toLocaleString()}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Signers List */}
              <div className="space-y-2">
                <h3 className="font-semibold flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Recipients ({signersList.length})
                  {signingMode === "all_at_once" ? (
                    <Badge variant="outline" className="text-xs font-normal">All receive at once</Badge>
                  ) : (
                    <Badge variant="outline" className="text-xs font-normal">Sequential signing</Badge>
                  )}
                </h3>
                <div className="space-y-2">
                  {signersList.length === 0 ? (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-amber-800">
                      <AlertCircle className="w-4 h-4 inline mr-2" />
                      No signers configured. The envelope may fail to send.
                    </div>
                  ) : (
                    signersList.map((signer, idx) => (
                      <div key={idx} className="bg-sand/50 rounded-lg p-4 flex items-center gap-4">
                        {signingMode === "in_order" ? (
                          <div className="w-8 h-8 rounded-full bg-forest/10 flex items-center justify-center text-forest font-semibold">
                            {idx + 1}
                          </div>
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-forest/10 flex items-center justify-center text-forest">
                            <User className="w-4 h-4" />
                          </div>
                        )}
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{signer.name}</span>
                            <Badge variant="outline" className="text-xs">
                              {signer.type}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
                            <Mail className="w-3 h-3" />
                            {signer.email}
                          </div>
                        </div>
                        {signingMode === "in_order" && idx < signersList.length - 1 && (
                          <ArrowRight className="w-4 h-4 text-muted-foreground" />
                        )}
                      </div>
                    ))
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  {signingMode === "all_at_once" 
                    ? "* All clients will receive the document simultaneously. Advisor will receive it to countersign after all clients have signed."
                    : "* Recipients will receive the document in the order shown above"
                  }
                </p>
              </div>

              {/* Warning */}
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-800">Important</p>
                    <p className="text-sm text-amber-700 mt-1">
                      Once sent, recipients will receive an email from DocuSign with a link to sign. 
                      You can track the status here, but cannot unsend the envelope.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setShowPreviewModal(false)}
              disabled={sending}
            >
              Cancel
            </Button>
            <Button
              className="bg-forest hover:bg-forest/90"
              onClick={confirmAndSend}
              disabled={sending || loadingDetails || signersList.length === 0}
              data-testid="confirm-send-docusign-btn"
            >
              {sending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send to {signersList.length} Recipient{signersList.length !== 1 ? 's' : ''}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document Preview Modal with Field Mappings */}
      <DocumentPreviewModal
        open={showDocumentPreview}
        onClose={() => setShowDocumentPreview(false)}
        proposalId={proposalId}
        token={token}
      />
    </>
  );
};

export default DocuSignTracker;
